import React from "react";
import MuiButton from "@mui/material/Button";
import type { ButtonProps as MuiButtonProps } from "@mui/material/Button";
import { alpha, styled } from "@mui/system";

export type ButtonVariant = "contained" | "outlined" | "text";

export interface ButtonProps extends Omit<MuiButtonProps, "variant"> {
  /**
   * The variant of the button
   * @default 'contained'
   */
  variant?: ButtonVariant;
  /**
   * If true, the button will have no elevation
   * @default false
   */
  flat?: boolean;
  /**
   * If true, the ripple effect will be disabled
   * @default false
   */
  disableRipple?: boolean;
  /**
   * If true, the button will take up the full width of its container
   * @default false
   */
  fullWidth?: boolean;
  /**
   * @default false
   */
  curved?: boolean;
  /**
   * The size of the button
   * @default 'medium'
   */
  size?: "small" | "medium" | "large";
  /**
   * The color of the button
   * @default 'secondary'
   */
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== "flat" && prop !== "curved",
})<ButtonProps>(({ theme, variant, color = "secondary", curved }) => ({
  // width: "100%",
  boxShadow: "none",
  textTransform: "capitalize",
  fontWeight: 500,
  fontSize: "14px",
  minHeight: "40px", // Set consistent minimum height

  // Consistent border handling
  //   border: `2px solid ${theme.palette[color].main}`,
  borderRadius: curved ? "10px" : theme.shape.borderRadius,

  // Consistent padding for all variants
  padding: `${theme.spacing(1)} ${theme.spacing(3)}`, // 12px 24px

  [theme.breakpoints.down("md")]: {
    // fontSize: "12px",
  },

  // Contained variant
  ...(variant === "contained" && {
    background: `linear-gradient(300deg, ${theme.palette.primary.main} -11%,
   ${theme.palette.secondary.main} 96%)`,
    color: theme.palette.common.white,
    // Remove border for contained or make it same color
    // border: `2px solid ${theme.palette[color].main}`, // Same border as background
  }),

  // Outlined variant
  ...(variant === "outlined" && {
    backgroundColor: "transparent",
    color: theme.palette[color].main,
    border: `2px solid ${theme.palette[color].main}`,
  }),

  // Text variant
  ...(variant === "text" && {
    border: "none",
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`, // Less padding for text variant
    minHeight: "auto", // Remove min-height for text variant
  }),

  "&:hover": {
    boxShadow: "none",
    ...(variant === "contained" && {
      // backgroundColor: theme.palette[color].dark,
      border: `2px solid ${theme.palette[color]}`,
    }),
    ...(variant === "outlined" && {
      // backgroundColor: theme.palette[color].light,
      border: `2px solid ${theme.palette[color]}`,
    }),
  },

  "&:active": {
    boxShadow: "none",
  },

  // "&.Mui-disabled": {
  //   boxShadow: "none",
  //   ...(variant === "contained" && {
  //     background: `${theme.palette.text.disabled} !important`,
  //     border: `2px solid ${theme.palette.text.disabled}`,
  //   }),
  //   ...(variant === "outlined" && {
  //     border: `2px solid ${theme.palette.text.disabled}`,
  //   }),
  //   color: alpha(theme.palette.common.white, 0.6),
  // },
  "&.Mui-disabled": {
    opacity: 0.8, // just reduce opacity
    pointerEvents: "none", // keep it non-clickable
    backgroundColor: "inherit", // keep original background
    // color: "inherit", // keep original text color
    border: "inherit", // keep original border
  },
}));

export const Button: React.FC<ButtonProps> = ({
  variant = "contained",
  disableRipple = false,
  flat = false,
  fullWidth = false,
  curved = false,
  size = "medium",
  color = "secondary",
  children,
  ...rest
}) => {
  return (
    <StyledButton
      variant={variant}
      disableRipple={disableRipple}
      flat={flat}
      fullWidth={fullWidth}
      size={size}
      color={color}
      curved={curved}
      {...rest}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
